import React, { useEffect, useMemo, useState } from 'react';
import * as S from '../Advances/AdvancesStyles';
import {
  getDetailedSalarySlip,
  listMonthlyDetailedPayslips,
} from '../../../../services/attendanceService';
import { getSupervisors } from '../../../../services/apiaryService';
import { toast } from 'react-toastify';
import AttendanceDrawer from './AttendanceDrawer';
import PayslipReviewModal from './PayslipReviewModal';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmt = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const resolveProjectId = (u) =>
  u?.projectId ||
  u?.adminMetaInfo?.projectId ||
  u?.userMetaInfo?.projectId ||
  (u?.fieldViewData && u.fieldViewData[0] && u.fieldViewData[0].project_id) ||
  (u?.projects && u.projects[0]) ||
  null;

const PayrollSlipTab = ({ projects }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [projectId, setProjectId] = useState('');
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [staff, setStaff] = useState([]);
  const [slips, setSlips] = useState({}); // userId -> detailed payslip | 'loading' | 'error'
  const [attendanceDrawerUser, setAttendanceDrawerUser] = useState(null); // { id, name, empCode }
  const [reviewSlip, setReviewSlip] = useState(null); // { userId, userName, employeeCode, payslip }

  // 1. Fetch Staff
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingStaff(true);
      const sups = await getSupervisors().catch(() => []);
      if (!cancelled) {
        setStaff(sups || []);
        setLoadingStaff(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Fetch any already finalized slips for the month
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listMonthlyDetailedPayslips(month, year).catch(() => null);
        if (!cancelled && res?.payslips) {
          const mapping = {};
          res.payslips.forEach((p) => {
            const uid = p.user_id?._id || p.user_id || p.userId;
            if (uid) mapping[String(uid)] = p;
          });
          setSlips((prev) => ({ ...prev, ...mapping }));
        }
      } catch (e) {
        console.error('Error fetching monthly slips:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const filteredStaff = useMemo(() => {
    if (!projectId) return staff;
    return staff.filter((s) => String(resolveProjectId(s)) === String(projectId));
  }, [staff, projectId]);

  const openAttendanceDrawer = (userId, userName, empCode) => {
    setAttendanceDrawerUser({ id: userId, name: userName, empCode });
  };

  const handleOpenReview = async (userId, userName, empCode) => {
    setSlips((prev) => ({ ...prev, [userId]: 'loading' }));
    try {
      const res = await getDetailedSalarySlip(userId, month, year);
      const payslip = res?.payslip || 'error';
      setSlips((prev) => ({ ...prev, [userId]: payslip }));
      if (payslip !== 'error') {
        setReviewSlip({
          userId,
          userName,
          employeeCode: empCode || payslip.employee_code || '',
          payslip,
        });
      } else {
        toast.error('Failed to compute salary slip for this employee.');
      }
    } catch (err) {
      console.error('Detailed payslip fetch failed:', err);
      setSlips((prev) => ({ ...prev, [userId]: 'error' }));
      toast.error('Failed to compute salary slip for this employee.');
    }
  };

  const handleAttendanceSaved = async () => {
    const { id: userId, name: userName, empCode } = attendanceDrawerUser;
    setAttendanceDrawerUser(null);
    await handleOpenReview(userId, userName, empCode);
  };

  const handleSlipFinalized = (finalizedPayload) => {
    if (finalizedPayload?.user_id) {
      setSlips((prev) => ({
        ...prev,
        [finalizedPayload.user_id]: {
          ...finalizedPayload,
          status: 'GENERATED',
        },
      }));
    }
  };

  const handleExportAllCsv = () => {
    const header = [
      'Employee Name',
      'Employee Code',
      'Month',
      'Year',
      'Total Days',
      'Paid Days',
      'LOP Days',
      'Monthly CTC',
      'Gross Salary Earned',
      'PF Employee',
      'ESIC Employee',
      'Advance Recovery',
      'TDS',
      'Total Deductions',
      'Net Take Home',
      'Reimbursements',
      'Final Take Home',
      'Status',
    ];

    const rows = filteredStaff
      .map((s) => {
        const uid = s._id || s.id;
        const slip = slips[uid];
        if (!slip || slip === 'loading' || slip === 'error') return null;

        const isDetailed = slip.gross_earned !== undefined;
        return [
          s.name || s.username || 'Staff',
          s.employee_id || slip.employee_code || '—',
          MONTHS[month - 1],
          year,
          slip.total_days_in_month || 30,
          slip.paid_days || slip.days_present || 0,
          slip.lop_days !== undefined ? slip.lop_days : slip.days_absent || 0,
          slip.monthly_ctc || 0,
          isDetailed ? slip.gross_earned : slip.gross_pay || 0,
          slip.employee_pf || 0,
          slip.employee_esic || 0,
          slip.salary_advance || 0,
          slip.tds || 0,
          isDetailed ? slip.total_deductions : slip.deductions || 0,
          isDetailed ? slip.net_take_home : slip.net_pay || 0,
          slip.reimbursement || 0,
          isDetailed ? slip.final_take_home : slip.net_pay || 0,
          slip.status || 'DRAFT',
        ];
      })
      .filter(Boolean);

    if (!rows.length) {
      toast.error('No calculated payslips to export yet for this month.');
      return;
    }

    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Statutory_Payroll_${MONTHS[month - 1]}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <S.FiltersBar>
        <div className="field">
          <label>Month</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear(), now.getFullYear() - 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="field grow">
          <label>Project</label>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">All Projects</option>
            {(projects || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <S.Btn ghost onClick={handleExportAllCsv} disabled={!filteredStaff.length}>
          Export Full Payroll CSV
        </S.Btn>
      </S.FiltersBar>

      <div
        style={{
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12.5,
          color: '#1E40AF',
          marginBottom: 14,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>
          💡 <strong>Statutory Payslip Workflow:</strong> Click <strong>"Generate / Preview"</strong> to view
          the complete live TeamNest breakdown (Basic, HRA, Bonus, PF, ESIC, Advances & Net Pay). You can edit CTC or any field and <strong>Finalize & Save</strong> the slip.
        </span>
      </div>

      <S.TableWrap>
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Emp Code</th>
              <th>Paid Days</th>
              <th>LOP</th>
              <th>Monthly CTC</th>
              <th>Gross Earned</th>
              <th>Deductions</th>
              <th>Net Take-Home</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loadingStaff ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                  Loading employees…
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                  No employees found for this project.
                </td>
              </tr>
            ) : (
              filteredStaff.map((s) => {
                const uid = s._id || s.id;
                const name = s.name || s.username || 'Staff';
                const empCode = s.employee_id || s.employee_code || '';
                const slip = slips[uid];
                const hasSlip = slip && slip !== 'loading' && slip !== 'error';
                const isFinalized =
                  hasSlip && (slip.status === 'GENERATED' || slip.status === 'PAID');

                const grossVal = hasSlip
                  ? slip.gross_earned !== undefined
                    ? slip.gross_earned
                    : slip.gross_pay
                  : 0;
                const dedVal = hasSlip
                  ? slip.total_deductions !== undefined
                    ? slip.total_deductions
                    : slip.deductions
                  : 0;
                const netVal = hasSlip
                  ? slip.final_take_home !== undefined
                    ? slip.final_take_home
                    : slip.net_take_home !== undefined
                    ? slip.net_take_home
                    : slip.net_pay
                  : 0;

                return (
                  <tr key={uid}>
                    <td>
                      <span
                        className="member-link"
                        style={{ cursor: 'pointer', fontWeight: 700, color: '#1E293B' }}
                        onClick={() => openAttendanceDrawer(uid, name, empCode)}
                      >
                        {name}
                      </span>
                    </td>
                    <td>
                      {empCode ? (
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            color: '#2563EB',
                            background: '#EFF6FF',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid #BFDBFE',
                          }}
                        >
                          {empCode}
                        </span>
                      ) : (
                        <span style={{ color: '#94A3B8' }}>—</span>
                      )}
                    </td>

                    {hasSlip ? (
                      <>
                        <td>
                          <strong style={{ color: '#059669' }}>
                            {slip.paid_days !== undefined
                              ? slip.paid_days
                              : slip.days_present || 0}
                          </strong>
                        </td>
                        <td>
                          <span
                            style={{
                              color:
                                (slip.lop_days || slip.days_absent || 0) > 0
                                  ? '#DC2626'
                                  : '#64748B',
                              fontWeight:
                                (slip.lop_days || slip.days_absent || 0) > 0 ? 700 : 400,
                            }}
                          >
                            {slip.lop_days !== undefined
                              ? slip.lop_days
                              : slip.days_absent || 0}
                          </span>
                        </td>
                        <td>{slip.monthly_ctc ? fmt(slip.monthly_ctc) : '—'}</td>
                        <td>
                          <strong>{fmt(grossVal)}</strong>
                        </td>
                        <td style={{ color: '#DC2626' }}>-{fmt(dedVal)}</td>
                        <td>
                          <strong style={{ color: '#059669', fontSize: 13.5 }}>
                            {fmt(netVal)}
                          </strong>
                        </td>
                        <td>
                          <span
                            style={{
                              padding: '2px 7px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              background: isFinalized ? '#DCFCE7' : '#FEF3C7',
                              color: isFinalized ? '#15803D' : '#B45309',
                            }}
                          >
                            {isFinalized ? 'FINALIZED' : 'DRAFT'}
                          </span>
                        </td>
                        <td>
                          <S.Btn
                            ghost
                            sm
                            onClick={() => handleOpenReview(uid, name, empCode)}
                            style={{ fontWeight: 600 }}
                          >
                            {isFinalized ? 'View Slip' : 'Review & Finalize'}
                          </S.Btn>
                        </td>
                      </>
                    ) : (
                      <>
                        <td colSpan={6} className="rowmuted">
                          {slip === 'loading'
                            ? 'Calculating breakdown…'
                            : slip === 'error'
                            ? 'Failed — click preview to retry'
                            : 'Not generated yet'}
                        </td>
                        <td>
                          <span
                            style={{
                              padding: '2px 7px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              background: '#F1F5F9',
                              color: '#64748B',
                            }}
                          >
                            PENDING
                          </span>
                        </td>
                        <td>
                          <S.Btn
                            sm
                            onClick={() => handleOpenReview(uid, name, empCode)}
                            disabled={slip === 'loading'}
                            style={{ background: '#2563EB', color: '#fff' }}
                          >
                            Generate / Preview
                          </S.Btn>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </S.TableWrap>

      <p style={{ fontSize: 12, color: '#9297A3', marginTop: 14 }}>
        Statutory calculation follows Indian Payroll Standards (50% Basic, 50% HRA, 8.33% Bonus,
        12% EPF ceiling ₹15,000, 0.75% ESIC threshold ₹21,000, and attendance proration).
      </p>

      <AttendanceDrawer
        show={!!attendanceDrawerUser}
        userId={attendanceDrawerUser?.id}
        userName={attendanceDrawerUser?.name}
        month={month}
        year={year}
        projectId={projectId || undefined}
        editable
        saveLabel="Save & Compute Breakdown"
        onClose={() => setAttendanceDrawerUser(null)}
        onSaved={handleAttendanceSaved}
      />

      <PayslipReviewModal
        show={!!reviewSlip}
        userId={reviewSlip?.userId}
        userName={reviewSlip?.userName}
        employeeCode={reviewSlip?.employeeCode}
        month={month}
        year={year}
        initialPayslip={reviewSlip?.payslip}
        onClose={() => setReviewSlip(null)}
        onFinalized={handleSlipFinalized}
      />
    </div>
  );
};

export default PayrollSlipTab;
